import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr'; 
import './style/OfferDetails.css';

// --- Custom Select Component ---
const CustomSelect = ({ options, value, onChange, placeholder }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedLabel = options.find(o => o.value === value)?.label || placeholder;

    return (
        <div className="volt-select-wrapper" ref={containerRef}>
            <div className={`volt-select-toggle ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <span>{selectedLabel}</span>
                <span className="volt-arrow-icon">▼</span>
            </div>
            {isOpen && (
                <div className="volt-select-menu">
                    {options.map((opt) => (
                        <div
                            key={opt.value}
                            className={`volt-select-item ${value === opt.value ? 'selected' : ''}`}
                            onClick={() => { onChange(opt.value); setIsOpen(false); }}
                        >
                            {opt.label}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const OfferDetails = () => {
    const { offerId } = useParams();
    const navigate = useNavigate();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [offer, setOffer] = useState(null);
    const [events, setEvents] = useState([]);
    const [postbacks, setPostbacks] = useState([]);

    // Description Toggle
    const [isDescExpanded, setIsDescExpanded] = useState(false);

    // Tracking Link State
    const [baseTrackingLink, setBaseTrackingLink] = useState('');
    const [allowedKeys, setAllowedKeys] = useState([]); // Keys from backend
    const [trackingParams, setTrackingParams] = useState([]); // User added params

    // Modal State
    const [showAddPbModal, setShowAddPbModal] = useState(false);
    const [showViewPbModal, setShowViewPbModal] = useState(false);
    const [currentPbView, setCurrentPbView] = useState('');

    // New Postback Form
    const [newPbData, setNewPbData] = useState({ event_id: '', url: '' });
    const [addPbLoading, setAddPbLoading] = useState(false);

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    // --- Fetch Data ---
    const fetchData = async () => {
        if (!offerId) {
            navigate('/offers');
            return;
        }

        const authToken = getCookie('authToken');
        try {
            setLoading(true);

            // 1. Auth
            const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', { action: 'checkAuth', authToken });
            if (!authRes.data.status) {
                navigate('/login');
                return;
            }
            setUser(authRes.data.res);

            // 2. Offer Details
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'getOfferDetails',
                authToken,
                offerId
            });

            if (res.data.status) {
                setOffer(res.data.offer);
                setEvents(res.data.events || []);
                setPostbacks(res.data.postbacks || []);
                setBaseTrackingLink(res.data.tracking_link);
                setAllowedKeys(res.data.allowed_keys || []);
            } else {
                toast.error(res.data.message || 'Failed to load offer details');
                navigate('/offers');
            }
        } catch (error) {
            toast.error('Failed to load offer details');
            navigate('/offers'); 
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [offerId]);

    // --- Tracking Link Logic ---
    const addParamField = () => {
        const usedKeys = trackingParams.map(p => p.key);
        const nextKey = allowedKeys.find(k => !usedKeys.includes(k));

        if (nextKey) {
            setTrackingParams([...trackingParams, { key: nextKey, value: '' }]);
        } else {
            toast.warning('Maximum tracking parameters reached.');
        }
    };

    const updateParamValue = (index, val) => {
        const newParams = [...trackingParams];
        newParams[index].value = val;
        setTrackingParams(newParams);
    };

    const removeParam = (index) => {
        const newParams = trackingParams.filter((_, i) => i !== index);
        setTrackingParams(newParams);
    };

    // Calculate Final Link
    const finalLink = useMemo(() => {
        let link = baseTrackingLink;
        trackingParams.forEach(p => {
            if (p.key && p.value) {
                const separator = link.includes('?') ? '&' : '?';
                link += `${separator}${p.key}=${encodeURIComponent(p.value)}`;
            }
        });
        return link;
    }, [baseTrackingLink, trackingParams]);

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
    };

    // --- Postback Logic ---
    const handleAddPostback = async () => {
        if (!newPbData.url) return toast.warning('Enter Postback URL');
        if (!newPbData.event_id) return toast.warning('Please select an event');

        setAddPbLoading(true);
        const authToken = getCookie('authToken');
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'addPostback',
                authToken,
                offerId,
                eventId: newPbData.event_id,
                url: newPbData.url
            });

            if (res.data.status) {
                toast.success('Postback added');
                setShowAddPbModal(false);
                setNewPbData({ event_id: '', url: '' });
                const evtName = events.find(e => e.id === newPbData.event_id)?.name || 'Unknown';
                const newId = res.data.new_id || Date.now();
                setPostbacks([...postbacks, { id: newId, url: newPbData.url, event_name: evtName }]);
            } else {
                toast.error(res.data.message || 'Failed');
            }
        } catch (err) {
            toast.error('Network Error');
        } finally {
            setAddPbLoading(false);
        }
    };

    const handleDeletePostback = async (pbId, event_id) => {
        if (!window.confirm("Delete this postback?")) return;
        
        const authToken = getCookie('authToken');
        try {
            const res = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'deletePostback',
                authToken,
                offerId,
                id: pbId,
                event_id
            });

            if (res.data.status) {
                setPostbacks(postbacks.filter(p => p.id !== pbId));
                toast.success('Postback deleted');
            } else {
                toast.error(res.data.message || 'Failed to delete postback');
            }
        } catch (error) {
            console.error(error);
            toast.error('Network error during deletion');
        }
    };

    if (loading) return <Loader />;

    return (
        <div className="volt-page-wrapper">
            <div className="volt-bg-fx volt-fx-primary"></div>
            <div className="volt-bg-fx volt-fx-secondary"></div>
            <Navbar name={user?.name} role={user?.role} />

            <div className="volt-main-container">

                {/* 1. Header / Basic Details */}
                <div className="volt-glass-card volt-header-pad">
                    <div className="volt-hero-row">
                        <div className="volt-logo-box">
                            <img src={offer.logo} alt="" className="volt-thumb-img" />
                        </div>
                        <div className="volt-info-block">
                            <h1>{offer.name} <span className="volt-id-tag">#{offer.id}</span></h1>
                            <div className="volt-stats-grid">
                                <div className="volt-stat-item">
                                    <span className="volt-lbl">Category</span>
                                    <span className="volt-val">{offer.category}</span>
                                </div>
                                <div className="volt-stat-item">
                                    <span className="volt-lbl">Type</span>
                                    <span className="volt-val volt-hl-purple">{offer.type}</span>
                                </div>
                                <div className="volt-stat-item">
                                    <span className="volt-lbl">Total Payout</span>
                                    <span className="volt-val volt-hl-money">₹{offer.payout}</span>
                                </div>
                                <div className="volt-stat-item">
                                    <span className="volt-lbl">Created</span>
                                    <span className="volt-val">{offer.created}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Description */}
                <div className="volt-glass-card">
                    <h3 className="volt-section-head">Description</h3>
                    <div className={`volt-desc-body ${isDescExpanded ? 'is-open' : 'is-closed'}`}>
                        {offer.description}
                    </div>
                    {offer.description.length > 150 && (
                        <button className="volt-toggle-btn" onClick={() => setIsDescExpanded(!isDescExpanded)}>
                            {isDescExpanded ? 'View Less ▲' : 'View More ▼'}
                        </button>
                    )}
                </div>

                {/* 3. Events Table */}
                <div className="volt-glass-card">
                    <h3 className="volt-section-head">Offer Goals / Events</h3>
                    <div className="volt-table-wrap">
                        <table className="volt-data-table">
                            <thead>
                                <tr>
                                    <th>Event Name</th>
                                    <th>Payout</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((evt, idx) => (
                                    <tr key={idx}>
                                        <td>{evt.name}</td>
                                        <td className="volt-td-green">₹{evt.payout}</td>
                                        <td><span className="volt-badge-fixed">FIXED</span></td>
                                    </tr>
                                ))}
                                {events.length === 0 && <tr><td colSpan="3" className="volt-empty-state">No Specific Events</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 4. Tracking Link Builder */}
                <div className="volt-glass-card">
                    <h3 className="volt-section-head">Tracking Link</h3>
                    <div className="volt-tracker-module">

                        {/* Final Link Display */}
                        <div className="volt-link-display">
                            <input type="text" value={finalLink} readOnly />
                            <button className="volt-copy-btn" onClick={() => copyToClipboard(finalLink)}>📋</button>
                        </div>

                        {/* Add Params Button */}
                        <div className="volt-actions-row">
                            <button className="volt-add-param-btn" onClick={addParamField}>
                                + Add Tracking Parameter
                            </button>
                        </div>

                        {/* Dynamic Inputs */}
                        {trackingParams.length > 0 && (
                            <div className="volt-params-stack">
                                {trackingParams.map((param, index) => (
                                    <div key={index} className="volt-param-row">
                                        
                                        {/* Key Display as Label */}
                                        <span className="volt-key-label">{param.key}</span>
                                        
                                        {/* Value Input */}
                                        <div className="volt-input-group">
                                            <input
                                                type="text"
                                                placeholder={`Enter value for ${param.key}`}
                                                value={param.value}
                                                onChange={(e) => updateParamValue(index, e.target.value)}
                                            />
                                            <button className="volt-del-param-btn" onClick={() => removeParam(index)}>&times;</button>
                                        </div>

                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* 5. Postbacks */}
                <div className="volt-glass-card">
                    <div className="volt-card-top-flex">
                        <h3 className="volt-section-head">Postbacks</h3>
                        <button className="volt-btn-primary volt-add-trigger" onClick={() => setShowAddPbModal(true)}>
                            + Add Postback
                        </button>
                    </div>

                    <div className="volt-table-wrap">
                        <table className="volt-data-table">
                            <thead>
                                <tr>
                                    <th>Event</th>
                                    <th>Postback URL</th>
                                    <th style={{ textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {postbacks.map((pb) => (
                                    <tr key={pb.id}>
                                        <td>{pb.eventName}</td>
                                        <td className="volt-td-code">{pb.url}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                className="volt-icon-btn is-view"
                                                onClick={() => { setCurrentPbView(pb.url); setShowViewPbModal(true); }}
                                            >👁</button>
                                            <button
                                                className="volt-icon-btn is-del"
                                                onClick={() => handleDeletePostback(pb.id)}
                                            >🗑</button>
                                        </td>
                                    </tr>
                                ))}
                                {postbacks.length === 0 && <tr><td colSpan="3" className="volt-empty-state">No postbacks set.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>

            {/* --- ADD POSTBACK MODAL --- */}
            {showAddPbModal && (
                <div className="volt-modal-backdrop" onClick={() => setShowAddPbModal(false)}>
                    <div className="volt-modal-box volt-box-pb" onClick={e => e.stopPropagation()}>
                        <div className="volt-modal-head">
                            <h3>Add Postback</h3>
                            <button onClick={() => setShowAddPbModal(false)}>&times;</button>
                        </div>
                        <div className="volt-modal-body">

                            <div className="volt-field-group">
                                <label>Select Event</label>
                                <CustomSelect
                                    placeholder="Select Event"
                                    options={events.map(e => ({ value: e.id, label: e.name }))}
                                    value={newPbData.event_id}
                                    onChange={(val) => setNewPbData({ ...newPbData, event_id: val })}
                                />
                            </div>

                            <div className="volt-field-group">
                                <label>Postback URL</label>
                                <textarea
                                    className="volt-txt-area"
                                    placeholder="https://yoursite.com/callback?click_id={aff_click_id}"
                                    value={newPbData.url}
                                    onChange={(e) => setNewPbData({ ...newPbData, url: e.target.value })}
                                ></textarea>
                            </div>

                            <div className="volt-macro-help">
                                <p>Available Macros:</p>
                                <div className="volt-tags-list">
                                    <span>{`{aff_click_id}`}</span>
                                    <span>{`{sub_aff_id}`}</span>
                                    <span>{`{offer_id}`}</span>
                                    <span>{`{event}`}</span>
                                    <span>{`{ip}`}</span>
                                    <span>{`{aff_sub1}...{aff_sub6}`}</span>
                                </div>
                            </div>

                            <button className="volt-btn-full" onClick={handleAddPostback} disabled={addPbLoading}>
                                {addPbLoading ? 'Saving...' : 'Save Postback'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- VIEW POSTBACK MODAL --- */}
            {showViewPbModal && (
                <div className="volt-modal-backdrop" onClick={() => setShowViewPbModal(false)}>
                    <div className="volt-modal-box volt-box-pb" onClick={e => e.stopPropagation()}>
                        <div className="volt-modal-head">
                            <h3>View Postback</h3>
                            <button onClick={() => setShowViewPbModal(false)}>&times;</button>
                        </div>
                        <div className="volt-modal-body center-align">
                            <div className="volt-code-display">
                                {currentPbView}
                            </div>
                            <button className="volt-btn-full" onClick={() => { copyToClipboard(currentPbView); setShowViewPbModal(false); }}>
                                Copy Link
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OfferDetails;